import React, { useState } from 'react';
import { useVisibilityObserver } from '@noths/polaris-client-react-hooks';
import { visuallyHidden } from '@noths/polaris-client-styles';

import { Accordion } from 'src/components/molecules/Accordion';
import * as elementIds from '../constants/elementIds';
import { FilterOptionsContainer } from '../containers/FilterOptionsContainer';
import { FilterWithFilterOptionsCount } from '../modules/selectors';
import * as styles from '../styles/FilterList.styles';
import { smallPill } from '../styles/smallPill.styles';

const VISIBILITY_THRESHOLD = 0.7;

interface Props {
  filter: FilterWithFilterOptionsCount;
  isExpanded: boolean;
  onFilterListItemVisibility: (filterUid: string) => void;
}

export const CollapsibleFilterListItem = ({
  filter,
  isExpanded,
  onFilterListItemVisibility,
}: Props) => {
  const [isOpen, setIsOpen] = useState(isExpanded);

  const { observedElementRef: listItemRef } = useVisibilityObserver<HTMLLIElement>(
    () => onFilterListItemVisibility(filter.uid),
    { observerOptions: { threshold: VISIBILITY_THRESHOLD }, autoUnobserve: true },
  );

  const getHeadingContent = () => (
    <>
      <span data-testid={elementIds.FILTER_LIST_ITEM_COUNT} css={[smallPill, styles.filterCount]}>
        {filter.selectedFilterOptionsCount}
      </span>
      <span css={visuallyHidden}> selected</span>
    </>
  );

  return (
    <li
      css={styles.collapsibleListItem}
      data-is-expanded={isOpen}
      ref={listItemRef}
      data-has-expandable-options={isExpanded && filter.options.length > 5}
      data-testid={`collapsiblelistitem-${filter.title}`}
    >
      <Accordion
        onToggleOpen={(updatedIsOpenState) => setIsOpen(updatedIsOpenState)}
        title={filter.title}
        titleStyles={styles.filterAccordionTitle}
        extraHeadingContent={filter.selectedFilterOptionsCount > 0 ? getHeadingContent() : null}
        id={`${filter.uid}-accordion`}
        renderHeading={({ children }) => <h3 css={styles.filterAccordionHeading}>{children}</h3>}
        isOpen={isExpanded}
        headingLevel="h3"
        buttonStyles={styles.filterArrowButton}
      >
        <FilterOptionsContainer key={filter.uid} filter={filter} isExpanded={isExpanded} />
      </Accordion>
    </li>
  );
};
